import {
	Component,

	HostListener,
	Injectable,

	OnChanges,
	OnDestroy,
	OnInit,

	SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import * as Reducers from '@app/store/reducers';
import { cast, IQuestionnaireGroup, Messgeraete } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { select, Store } from '@ngrx/store';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { Subscription } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { AbstractSpecialQuestionComponent, ISpecialQuestion, ISpecialQuestionComponent } from '../special-question.service';
import { isNil } from 'lodash';
import { InputService } from '@app/services/input/input.service';
import {
	LandoltTestEpilogComponent
} from '@workflow/components/questionnaire/questionnaire-item/special-question/landolt-test/landolt-test-epilog.component';
import {
	LandoltElement, LandoltRingHelper,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/landolt-test/landolt-rings';

interface LandoltResult {
	zeit: number;
	fehler: number;
	selected?: boolean[];
	fehlwert?: boolean;
}

@Injectable()
export class LandoltTest implements ISpecialQuestion {
	public readonly component = LandoltTestComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'LandoltTest';
	public readonly requiredDevices = [Messgeraete.TouchScreen];

	title = 'Landolt Test';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: 'Landolt Test',
		preface: [
			`<div class="markdown-html">
				Auf dem nachfolgenden Testbogen sind 600 Landolt-Ringe zeilenartig angeordnet, wobei die Öffnung der Ringe in verschiedene Richtungen zeigt.
				Gehen Sie Zeile für Zeile vor. Finden Sie so schnell und genau wie möglich alle Kreise, deren Öffnung nach oben zeigt.
				Beachten Sie, dass es auf Genauigkeit und auf Schnelligkeit ankommt. Sie beginnen den Test durch Betätigen der “Test starten” Schaltfläche.
				<div class="centered-rel pt-3">
					<img src="assets/images/icons/landolt_C_slim_u.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_d.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_l.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_r.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_d.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_l.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_u.svg" alt="Beispiel" width="40px"/>
					<img src="assets/images/icons/landolt_C_slim_r.svg" alt="Beispiel" width="40px"/>
				</div>
			</div>`
		],
		items: [
			cast<BlockQuestionItem>({
				title: 'Landolt Test',
				type: 'BlockQuestion',
				items: [
					{
						key: 'zeit',
						label: 'Zeit',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 400,
							steps: 0.1,
							unit: 'sek',
						})
					},
					{
						key: 'fehler',
						label: 'Fehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 20,
						})
					}
				],
				isProcedure: true,
			})
		]
	};

	public isCompleted(currentValue: LandoltResult): boolean {
		return !isNil(currentValue?.zeit) && !isNil(currentValue?.fehler) || currentValue?.fehlwert;
	}

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: specialQuestion,
			suppressDescription: true,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
			},
			suppressDescription: true,
			subpage: 2,
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			allowNextPage: true,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: LandoltTestEpilogComponent,
			key: specialQuestion.key,
		}];
	}
}

@Component({
	selector: 'workflow-landolt-test',
	templateUrl: './landolt-test.component.html',
	styleUrls: ['./landolt-test.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class LandoltTestComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, OnChanges, ISpecialQuestionComponent {

	numLandoltElements: number;
	results: LandoltResult = {
		zeit: 0,
		fehler: 20,
		selected: [],
	};
	startTime: number;
	public timer = 0;
	private timerInterval;

	landoltElements = new Array<LandoltElement>();
	landoltTest = new LandoltTest();

	private subscription: Subscription = new Subscription();

	constructor(
		private workflowItemKey: WorkflowItemKeyDirective,
		private inputService: InputService,
		router: Router
	) {
		super(router);
	}

	ngOnInit(): void {
		this.landoltElements = LandoltRingHelper.getLandoltRingMatrix();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.inputService.suspendDetectionSwipe = false;
		clearInterval(this.timerInterval);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.page) {
			if (changes.page.previousValue === 1 && changes.page.currentValue === 2) {
				this.resultsUpdated();
			}

			this.startTime = performance.now();
			this.results = {zeit: null, fehler: null, selected: []};
			this.timerInterval = setInterval(() => this.timer = performance.now() - this.startTime, 500);
			this.inputService.suspendDetectionSwipe = true;

		}
	}

	async dispatchVitaReact($event: Event): Promise<void> {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-press', ['$event'])
	vitaReactPress($event: Event): void {
		this.hitButton($event);
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: Event): void {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	toggleSelected(index: number, $event: MouseEvent): void {
		const target = $event.target as HTMLElement;
		if (target.classList.contains('landolt-button')) {
			target.classList.toggle('landolt-button-selected');
		} else {
			target.parentElement.classList.toggle('landolt-button-selected');
		}
		this.landoltElements[index].selected = !this.landoltElements[index].selected;
	}

	resultsUpdated(): void {
		if (this.question?.key && this.results && this.workflowItemKey && !this.finishedAndLocked) {
			this.workflowItemKey.setProperty(this.results);
		}
	}

	private hitButton(event: Event): void {
		this.calculateResult(event);
		this.resultsUpdated();
		this.goToEpilog();
	}

	private calculateResult(event: Event): void {
		this.calculateResultTimestamp(event);
		this.calculateResultFehler();
		this.calculateResultSelectedFields();
	}

	private calculateResultTimestamp(event: Event): void {
		const deltaTime = event.timeStamp - this.startTime;
		const timeInSeconds3Digits = Math.floor(deltaTime / 10) / 100;
		this.results.zeit = Math.max(0, timeInSeconds3Digits);
	}

	private calculateResultFehler() {
		// Anzahl n. ausgewählter Elemente die nach oben zeigen - falsch geklickt wird ignoriert
		this.results.fehler = this.landoltElements.filter(x => !x.selected && x.value === 0).length;
	}

	private calculateResultSelectedFields() {
		this.results.selected = this.landoltElements.map(el => el.selected);
	}
}
